import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Trees, Award, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SERVICES } from "@/lib/constants";
import { apiClient } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";

const HERO_IMG = "https://images.unsplash.com/photo-1611021061285-16c871740efa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHx3b29kd29ya2luZyUyMGNyYWZ0c21hbnxlbnwwfHx8fDE3ODExNzc0OTV8MA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
    const { settings } = useSettings();
    const [testimonials, setTestimonials] = useState([]);

    useEffect(() => {
        apiClient.get("/testimonials?approved_only=true").then((r) => setTestimonials(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section data-testid="hero-section" className="relative h-[92vh] min-h-[640px] w-full overflow-hidden">
                <img src={HERO_IMG} alt="Jassi Wood Works workshop" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/55" />
                <div className="relative h-full container-padded flex flex-col justify-end pb-20 md:pb-28">
                    <div className="max-w-3xl">
                        <div className="overline text-walnut-light/90 mb-6" data-testid="hero-overline">Since 2008 · Mohali, Punjab</div>
                        <h1 data-testid="hero-title" className="font-serif text-5xl sm:text-6xl lg:text-7xl text-cream leading-[1.05] tracking-tight">
                            {settings?.tagline ? settings.tagline.split(",").map((part, i) => (
                                <span key={i}>{part.trim()}{i === 0 ? "," : ""}<br /></span>
                            )) : <>Bespoke woodcraft,<br />built to last generations.</>}
                        </h1>
                        <p data-testid="hero-subtitle" className="mt-6 text-base md:text-lg text-stone-200 max-w-xl leading-relaxed">
                            Solid-wood furniture, modular kitchens, carved doors and full interior millwork — designed in our studio and hand-built in our Mohali workshop.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact#quote" data-testid="hero-cta-quote" className="bg-walnut text-cream px-8 py-4 text-sm tracking-wider uppercase hover:bg-walnut-dark transition-colors inline-flex items-center gap-2">
                                Get a Quote <ArrowRight size={16} />
                            </Link>
                            <Link to="/contact" data-testid="hero-cta-contact" className="border border-cream/80 text-cream px-8 py-4 text-sm tracking-wider uppercase hover:bg-cream hover:text-walnut transition-colors">
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* INTRO */}
            <section className="section-spacing container-padded">
                <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
                    <div className="md:col-span-5">
                        <div className="overline mb-4">Our Practice</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight">
                            The slow craft of building things that stay.
                        </h2>
                    </div>
                    <div className="md:col-span-7 text-stone-muted text-lg leading-relaxed">
                        <Reveal>
                            <p>
                                {settings?.business_name || "Jassi Wood Works"} is a family-run studio of carpenters, finishers and designers. For over seventeen years we've quietly built furniture, kitchens, doors and interiors for homes across north India — choosing the right grain by hand, cutting tight mortise-and-tenon joints, and finishing each surface with multiple coats of polish.
                            </p>
                            <p className="mt-6">
                                We don't believe in templates. Every piece begins with a conversation, a sketch, and a slab of timber chosen for your room.
                            </p>
                        </Reveal>
                    </div>
                </div>

                {/* Values */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mt-20">
                    {[
                        { icon: Trees, t: "Solid Timber", d: "Teak, sheesham, oak & walnut — sourced from sustainable mills." },
                        { icon: Hammer, t: "Hand-Joined", d: "Traditional joinery for furniture that doesn't loosen with time." },
                        { icon: Award, t: "17+ Years", d: "A reputation built one project, one referral at a time." },
                        { icon: Sparkles, t: "Site-Finished", d: "Our team installs and finishes — no third-party hand-offs." },
                    ].map((v, i) => (
                        <Reveal key={v.t} delay={i * 100}>
                            <div className="border-t border-walnut/30 pt-6">
                                <v.icon size={26} className="text-walnut" />
                                <div className="font-serif text-2xl text-ink mt-4">{v.t}</div>
                                <p className="text-sm text-stone-muted mt-2 leading-relaxed">{v.d}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section className="section-spacing bg-sand">
                <div className="container-padded">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                        <div>
                            <div className="overline mb-3">What We Build</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight max-w-2xl">A studio with a workshop attached.</h2>
                        </div>
                        <Link to="/services" data-testid="home-services-link" className="text-walnut hover:text-walnut-light inline-flex items-center gap-2 text-sm tracking-wide">
                            All services <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {SERVICES.slice(0, 6).map((s, i) => (
                            <Reveal key={s.slug} delay={i * 80}>
                                <Link to="/services" className="group block bg-cream overflow-hidden">
                                    <div className="aspect-[4/5] overflow-hidden">
                                        <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    </div>
                                    <div className="p-6">
                                        <div className="font-serif text-2xl text-ink">{s.title}</div>
                                        <p className="text-sm text-stone-muted mt-2 leading-relaxed">{s.body}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="section-spacing container-padded" data-testid="testimonials-section">
                <div className="overline mb-4">Kind Words</div>
                <h2 className="font-serif text-4xl md:text-5xl text-ink mb-14 max-w-2xl">From the people who live with our work.</h2>
                <div className="grid md:grid-cols-3 gap-8">
                    {testimonials.map((t, i) => (
                        <Reveal key={t.id || t.name} delay={i * 100}>
                            <figure className="border-t border-walnut/30 pt-8 h-full flex flex-col">
                                <blockquote className="font-serif text-2xl text-ink leading-snug flex-1">
                                    "{t.quote}"
                                </blockquote>
                                <figcaption className="mt-6">
                                    <div className="font-medium text-ink">{t.name}</div>
                                    <div className="text-xs uppercase tracking-widest text-walnut-light mt-1">{t.role}</div>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                    {!testimonials.length && <div className="text-stone-muted col-span-full">Testimonials will appear here once approved.</div>}
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="bg-walnut text-cream py-20 md:py-24">
                <div className="container-padded flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                    <h2 className="font-serif text-4xl md:text-5xl leading-tight max-w-2xl">Have a project in mind? Let's make it.</h2>
                    <Link to="/contact#quote" data-testid="home-bottom-cta" className="bg-cream text-walnut px-8 py-4 text-sm tracking-wider uppercase hover:bg-sand transition-colors inline-flex items-center gap-2 self-start md:self-auto">
                        Request a free quote <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
